import { normalize } from './spelling';

// Teacher-style markup of a misspelling: what the child wrote, with the runs that
// need fixing called out. Segments, in reading order:
//   { type: 'same', text }              letters that were right
//   { type: 'change', wrong, right }    strike out `wrong`, write `right` above it;
//                                       wrong === '' → a caret (letters missing),
//                                       right === '' → a plain strike (extra letters)
// Only letters are compared, like grading (normalize): case, accents, spaces and
// hyphens are never errors. `right` keeps the target's own characters (é, S).
export const diffSpelling = (attempt, target) => {
  const wrote = [...normalize(attempt)];
  const letters = [...target].map(text => ({ text, key: normalize(text) })).filter(unit => unit.key);

  // Edit-distance table, then walk back from the end. Ties prefer a substitution
  // (so a swap like "ie"/"ei" is one replaced run, not a delete plus an insert), then
  // an insert or delete over a match (so in "accross" the later, repeated c is the extra one).
  const d = Array.from({ length: wrote.length + 1 }, (_, i) => [i, ...Array(letters.length).fill(0)]);
  for (let j = 1; j <= letters.length; j += 1) d[0][j] = j;
  for (let i = 1; i <= wrote.length; i += 1) {
    for (let j = 1; j <= letters.length; j += 1) {
      d[i][j] = Math.min(d[i - 1][j - 1] + (wrote[i - 1] === letters[j - 1].key ? 0 : 1), d[i - 1][j] + 1, d[i][j - 1] + 1);
    }
  }
  const distance = d[wrote.length][letters.length];
  // An attempt that is mostly wrong would fragment into confetti; cross the whole word out instead.
  if (distance * 2 > Math.max(wrote.length, letters.length)) {
    return [{ type: 'change', wrong: wrote.join(''), right: target.trim() }];
  }

  const ops = [];
  for (let i = wrote.length, j = letters.length; i > 0 || j > 0;) {
    const diagonal = i > 0 && j > 0 ? d[i - 1][j - 1] + (wrote[i - 1] === letters[j - 1].key ? 0 : 1) : Infinity;
    if (diagonal === d[i][j] && wrote[i - 1] !== letters[j - 1].key) {
      ops.push({ wrong: wrote[i - 1], right: letters[j - 1].text });
      i -= 1; j -= 1;
    } else if (i > 0 && d[i - 1][j] + 1 === d[i][j]) {
      ops.push({ wrong: wrote[i - 1], right: '' });
      i -= 1;
    } else if (j > 0 && d[i][j - 1] + 1 === d[i][j]) {
      ops.push({ wrong: '', right: letters[j - 1].text });
      j -= 1;
    } else {
      ops.push({ same: wrote[i - 1] });
      i -= 1; j -= 1;
    }
  }

  // Adjacent edits form one run, as a teacher would strike "ie" and write "ei" in one go.
  const segments = [];
  ops.reverse().forEach(op => {
    const last = segments[segments.length - 1];
    if ('same' in op) {
      if (last?.type === 'same') last.text += op.same;
      else segments.push({ type: 'same', text: op.same });
    } else if (last?.type === 'change') {
      last.wrong += op.wrong; last.right += op.right;
    } else {
      segments.push({ type: 'change', wrong: op.wrong, right: op.right });
    }
  });
  return segments;
};
